import type { PlayerApi, PlayerConfig } from '@movingimage-evp/player-library';
import type { HTMLAttributes } from 'react';

import type { LsproPlayerConfig } from '../../use-player-config';

import { Spinner } from '@movingimage-evp/mi-ui-component-library';
import { Player, PlayerEvent, SourceType } from '@movingimage-evp/player-library';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchVideoDetails } from '../../../../../common/fetch-video-details';
import { type ECdnSettings, type VodChapter } from '../../../../../generated/graphql-viewer';
import { devLog } from '../../../../../utils';
import { useSelectedLanguage } from '../../../../providers/selected-language-provider';
import { usePlayerConfig } from '../../use-player-config';
import { UnmutePopup } from '../unmute-popup';

import styles from './cover-video-player.module.css';

type CoverVideoPlayerProps = {
  webcastId: string;
  playoutUrl?: string;
  videoId?: string;
  accountId?: string;
  lsproId?: string;
  videoToken?: string;
  title?: string;
  primaryColor?: string;
  iconColor?: string;
  logoUrl?: string;
  looping?: boolean;
  autoplay?: boolean;
  stepButtonsAreVisible?: boolean;
  state?: string;
  offsetInSeconds?: number;
  kollectiveConfiguration?: ECdnSettings['kollective'];
  chapters?: VodChapter[];
  language?: string;
  videoVodManifestUrl?: string;
  onPlayerReady?: (api: PlayerApi) => void;
  onPlayerTimeUpdate?: (currentTime: number) => void;
  onOverlayVisibilityChange?: (visible: boolean) => void;
} & HTMLAttributes<HTMLDivElement>;

export function CoverVideoPlayer({
  webcastId,
  playoutUrl,
  videoId,
  accountId,
  lsproId,
  videoToken,
  title,
  primaryColor,
  iconColor,
  logoUrl,
  looping,
  autoplay,
  stepButtonsAreVisible,
  state,
  offsetInSeconds,
  kollectiveConfiguration,
  chapters,
  language,
  videoVodManifestUrl,
  onPlayerReady,
  onPlayerTimeUpdate,
  onOverlayVisibilityChange,
  ...props
}: CoverVideoPlayerProps) {
  const { t } = useTranslation();
  const { selectedLanguage, primaryLanguage } = useSelectedLanguage();
  const playerApiRef = useRef<PlayerApi | null>(null);
  const initialVideoTokenRef = useRef(videoToken);

  const [videoDetails, setVideoDetails] = useState<PlayerConfig>();
  const [unmutePopupVisible, setUnmutePopupVisible] = useState(false);
  const [hasCoverVideoError, setHasCoverVideoError] = useState(false);

  const lsproPlayerConfig: LsproPlayerConfig | undefined = useMemo(() => {
    if (!videoDetails) return undefined;

    const youboraAnalytics = videoDetails.plugins?.youboraAnalytics;

    return {
      webcastId,
      contentId: videoId,
      playbackUrl: 'undefined',
      title,
      accountId,
      lsproId,
      state,
      language: language || selectedLanguage?.code,
      primaryLanguage: language || primaryLanguage?.code,
      channelId: youboraAnalytics?.['content.customDimension.3'],
      channelName: youboraAnalytics?.['content.channel'],
      groupId: youboraAnalytics?.['content.customDimension.6'],
      videoManagerId: youboraAnalytics?.['content.customDimension.1'],
      kollectiveConfiguration,
      chapters,
    };
  }, [
    webcastId,
    videoId,
    title,
    accountId,
    lsproId,
    state,
    language,
    kollectiveConfiguration,
    chapters,
    videoDetails,
    selectedLanguage?.code,
    primaryLanguage?.code,
  ]);

  const { plugins, chapters: chaptersConfig } = usePlayerConfig(lsproPlayerConfig) || {};

  const playerConfig = useMemo(() => {
    if (!videoDetails || !plugins) return undefined;

    return {
      ...videoDetails,
      chapters: chaptersConfig || videoDetails.chapters,
      plugins,
      settings: {
        primaryColor,
        iconColor,
        logoUrl,
        autoplay,
        looping,
        stepButtonsAreVisible,
        videoLayout: 'fill',
      },
      metadata: { ...videoDetails.metadata, title: title || '' },
      analyticsMode: 'full',
    } as PlayerConfig;
  }, [
    videoDetails,
    chaptersConfig,
    plugins,
    primaryColor,
    iconColor,
    logoUrl,
    autoplay,
    looping,
    stepButtonsAreVisible,
    title,
  ]);

  const addParams = useCallback(
    (url: string) => {
      if (!offsetInSeconds) return url;

      const newUrl = new URL(url);
      newUrl.searchParams.set('start', offsetInSeconds.toString());
      newUrl.searchParams.set('wowzadvrplayliststart', (offsetInSeconds * 1000).toString());
      newUrl.searchParams.set('wowzaplaystart', (offsetInSeconds * 1000).toString());
      return newUrl.toString();
    },
    [offsetInSeconds]
  );

  useEffect(() => {
    // Reset the state so that the Player is properly re-rendered when new IDs come in
    playerApiRef.current = null;
    setVideoDetails(undefined);
    setUnmutePopupVisible(false);
    setHasCoverVideoError(false);

    if (videoVodManifestUrl) {
      setVideoDetails({ sources: { hls: { src: addParams(videoVodManifestUrl), type: SourceType.HLS } } });
      return;
    }

    if (!playoutUrl || !videoId) return;

    fetchVideoDetails({ playoutUrl, videoId, videoToken: initialVideoTokenRef.current })
      .then(({ playerData }) => {
        if (playerData.sources.hls) playerData.sources.hls.src = addParams(playerData.sources.hls.src);

        setVideoDetails(playerData);

        devLog(
          'CoverVideoPlayer loaded video details',
          '\n request:',
          { playoutUrl, videoId, videoToken: initialVideoTokenRef.current },
          '\n response:',
          playerData
        );
      })
      .catch(() => setHasCoverVideoError(true));
  }, [addParams, playoutUrl, videoId, videoVodManifestUrl]);

  const handleVolumeChange = useCallback(() => {
    if (!playerApiRef.current?.getMuted()) setUnmutePopupVisible(false);
  }, []);

  const handleOnTimeUpdate = useCallback(() => {
    if (!onPlayerTimeUpdate || !playerApiRef.current) return;

    onPlayerTimeUpdate(playerApiRef.current.getCurrentTime());
  }, [onPlayerTimeUpdate]);

  const playVideo = useCallback(
    async (api: PlayerApi) => {
      try {
        if (api.getPaused()) await api.play();
      } catch {
        try {
          api.setMuted(true);
          setUnmutePopupVisible(true);
          api.addEventListener(PlayerEvent.VOLUME_CHANGE, handleVolumeChange);

          await api.play();
        } catch {
          setHasCoverVideoError(true);
        }
      }
    },
    [handleVolumeChange]
  );

  const unmuteVideo = () => {
    setUnmutePopupVisible(false);
    playerApiRef.current?.setMuted(false);
  };

  useEffect(() => {
    if (!unmutePopupVisible) {
      playerApiRef.current?.removeEventListener(PlayerEvent.VOLUME_CHANGE, handleVolumeChange);
    }
  }, [handleOnTimeUpdate, handleVolumeChange, unmutePopupVisible]);

  useEffect(() => {
    return playerApiRef.current?.removeEventListener(PlayerEvent.TIME_UPDATE, handleOnTimeUpdate);
  }, [handleOnTimeUpdate]);

  const playerReadyHandler = useCallback(
    (api: PlayerApi) => {
      if (playerApiRef.current) return;
      else playerApiRef.current = api;

      if (onPlayerReady) onPlayerReady(api);
      if (autoplay) playVideo(api);
      if (onPlayerTimeUpdate) api.addEventListener(PlayerEvent.TIME_UPDATE, handleOnTimeUpdate);
    },
    [autoplay, handleOnTimeUpdate, onPlayerTimeUpdate, playVideo, onPlayerReady]
  );

  useEffect(() => {
    if (playerConfig) devLog('⏯️ CoverVideoPlayer playerConfig', playerConfig, { memoize: true });
  }, [playerConfig]);

  return (
    <div data-testid="viewer-cover-video" {...props}>
      {!playerConfig && !hasCoverVideoError && <Spinner size={40} style={{ margin: 'auto' }} />}

      {playerConfig && (
        <Player {...playerConfig} onReady={playerReadyHandler} onOverlayVisibilityChange={onOverlayVisibilityChange} />
      )}

      {unmutePopupVisible && !hasCoverVideoError && (
        <UnmutePopup
          title={t('viewer.views.unmutePopup.title.videoStarted')}
          onClose={() => setUnmutePopupVisible(false)}
          onUnMute={unmuteVideo}
        />
      )}

      {hasCoverVideoError && (
        <div className={styles.coverVideoError}>
          {t('errors.coverVideo.noAccess')}
          <small>{t('errors.contactAdministrator')}</small>
        </div>
      )}
    </div>
  );
}
